// jdEvidence.js — WHICH John Deere rows may count as PRODUCTION / CLAIM evidence.
//
// One rule, shared by api/ and src/ (CommonJS, like yieldImport.js, so both can load it).
//
// `jd_field_operations` marks two kinds of operation that must not be counted:
//   gone       source_present = false — Deere stopped returning it (three walks, 24 h)
//   duplicate  is_duplicate   = true  — the same pass recorded twice
// Readers OF THAT TABLE already filter both (JD_LIVE_OPS, duplicateOpIds).
// `jd_field_ftf` — the CLU split every evidence screen reads — carries no such columns,
// so until this rule it kept counting both. Measured 2026-10-18 on metric=yield:
// gone 2 rows / 43 ac, duplicate 3,330 / 54,226 ac.
//
// ⚠ SANDBOX (source_host = 'sandbox', fetched from sandboxapi.deere.com) IS COUNTED, AND
// TAGGED. The first cut of this rule excluded it and that would have DELETED REAL DATA:
// measured 2026-10-19, for CY2025 KLUDT (338 ops / 14,343 ac), MEISENZAHL, SILSBY and
// TRIPLE H have ZERO production-host operations — sandbox is their ONLY machine record.
// Where both hosts exist (KENNEDY 2025: 80 sandbox ops / 2,104 ac vs 79 production /
// 1,999 ac) the field ids differ, so whether they are the same physical work is UNKNOWN.
// Rule zero: unknown changes no grower's data. So sandbox rows are KEPT exactly as today,
// counted under report.sandbox so a screen can say so, and where one farm has work from
// BOTH hosts in one crop season the report names it (report.bothHosts) — a possible double
// count for an admin to look at. Nothing is subtracted. Sandbox syncing stopped 2026-08-06.
//
// THREE RULES THAT FAIL SILENTLY IN THE OTHER DIRECTION:
//  1. A HAND-IMPORTED YIELD MAP HAS NO jd_field_operations ROW (manualOperationId mints
//     `manual:<farmer>:<file>`). It is KEPT, always — it is the grower's own evidence.
//  2. A machine row whose operation status could not be READ (RLS, a failed request) is
//     KEPT and COUNTED as unverified — never silently dropped, never silently trusted.
//     Dropping it would erase evidence on a read failure; that is the worse error.
//  3. Exclusion is REPORTED (rows and acres per reason). A total that quietly shrinks
//     and a short read look identical on screen.

const JD_EVIDENCE_STATUS_COLUMNS = ["jd_operation_id", "source_present", "is_duplicate", "source_host"];

function isManualOperationId(id) {
  return typeof id === "string" && id.startsWith("manual:");
}

// The reason an OPERATION row is not evidence, or null. Order is fixed so a row that is
// both gone and duplicate is reported once, under the stronger fact.
// Sandbox is NOT a reason — see the header. It is a tag (isJdSandbox).
function jdEvidenceExclusion(op) {
  if (!op) return null;
  if (op.source_present === false) return "gone";
  if (op.is_duplicate === true) return "duplicate";
  return null;
}

// Which John Deere host an operation came from. Only a STATED host counts; an operation
// with no host recorded is neither, so it can never trip the both-hosts flag.
function jdHostOf(op) {
  const h = String((op && op.source_host) || "").toLowerCase();
  return h === "sandbox" || h === "production" ? h : null;
}
function isJdSandbox(op) { return jdHostOf(op) === "sandbox"; }

// The crop season a row belongs to: the operation's own crop_season where it is carried,
// else the calendar year of its date (the year every pace in this app already scopes by).
function jdSeasonOf(r) {
  if (!r) return null;
  if (r.crop_season != null && r.crop_season !== "") return String(r.crop_season);
  const d = r.operation_date || r.operation_start;
  return d ? String(d).slice(0, 4) : null;
}

// Tracks which hosts each (farm, season) has COUNTED work from. A farm is the row's own
// farmer_id (the CLU owner on ftf, the operator on operations); a caller that is already
// one farm passes rows of one farm, so the key degrades to the season.
function hostTracker() {
  const seen = new Map();
  return {
    add(row, op) {
      const h = jdHostOf(op); const season = jdSeasonOf(row); if (!h || !season) return;
      const k = `${(row && row.farmer_id) || ""}|${season}`;
      (seen.get(k) || seen.set(k, new Set()).get(k)).add(h);
    },
    both() {
      const out = [];
      for (const [k, hs] of seen) if (hs.has("sandbox") && hs.has("production")) {
        const i = k.lastIndexOf("|");
        out.push({ farmer_id: k.slice(0, i) || null, season: k.slice(i + 1) });
      }
      return out;
    },
  };
}

// Distinct operation ids a set of ftf rows needs a status for — manual ids never need one.
function jdEvidenceOpIds(rows) {
  const s = new Set();
  for (const r of rows || []) {
    const id = r && r.jd_operation_id;
    if (id && !isManualOperationId(id)) s.add(String(id));
  }
  return [...s];
}

// gone / duplicate are REFUSED; sandbox / unverified / manual are KEPT and inside `kept`.
const REPORT_KEYS = ["gone", "duplicate", "sandbox", "unverified", "manual", "kept"];
function emptyReport() {
  const out = {};
  for (const k of REPORT_KEYS) out[k] = { rows: 0, acres: 0 };
  out.bothHosts = [];
  return out;
}
function roundReport(report) {
  for (const k of REPORT_KEYS) report[k].acres = Math.round(report[k].acres * 10) / 10;
  return report;
}

// Filter attribution rows (jd_field_ftf / jd_field_county) against operation statuses.
//   rows     rows carrying jd_operation_id (+ `acres`)
//   statuses Map(op id -> op row) or an array of op rows with JD_EVIDENCE_STATUS_COLUMNS
// Returns { rows: kept, report } — kept preserves input order.
//
// opts.tagOnly — for a payload that feeds a MAP as well as evidence. Nothing is dropped;
// every row is copied with `jd_excluded` = the reason it is not evidence (or null), and
// the report still counts each reason. The map keeps drawing what it drew; the evidence
// consumer filters on `jd_excluded`. Use isJdEvidenceRow() to read the tag.
function filterJdEvidence(rows, statuses, opts = {}) {
  const tagOnly = !!(opts && opts.tagOnly);
  const byId = statuses instanceof Map ? statuses
    : new Map((statuses || []).filter((o) => o && o.jd_operation_id).map((o) => [String(o.jd_operation_id), o]));
  const report = emptyReport();
  const hosts = hostTracker();
  const kept = [];
  const add = (k, r) => { report[k].rows++; report[k].acres += Number(r && r.acres) || 0; };
  for (const r of rows || []) {
    const id = r && r.jd_operation_id;
    const keep = (x) => kept.push(tagOnly ? { ...x, jd_excluded: null } : x);
    if (isManualOperationId(id)) { add("manual", r); add("kept", r); keep(r); continue; }
    const op = id ? byId.get(String(id)) : null;
    if (!op) { add("unverified", r); add("kept", r); keep(r); continue; }
    const why = jdEvidenceExclusion(op);
    if (why) { add(why, r); if (tagOnly) kept.push({ ...r, jd_excluded: why }); continue; }
    // SANDBOX IS KEPT — counted as today, and counted again under report.sandbox.
    if (isJdSandbox(op)) add("sandbox", r);
    hosts.add(r, op);
    add("kept", r); keep(r);
  }
  report.bothHosts = hosts.both();
  return { rows: kept, report: roundReport(report) };
}

// Reads the tag tagOnly writes. A row with no tag at all predates the tag and counts.
function isJdEvidenceRow(r) {
  return !!r && !r.jd_excluded;
}

// Filter OPERATION rows themselves (they carry the status columns). A row that does not
// carry them cannot be judged; the caller must SELECT them (JD_EVIDENCE_STATUS_COLUMNS).
function filterJdEvidenceOps(ops) {
  const report = emptyReport();
  const hosts = hostTracker();
  const kept = [];
  for (const o of ops || []) {
    const why = jdEvidenceExclusion(o);
    const ac = Number(o && o.area_acres) || 0;
    if (why) { report[why].rows++; report[why].acres += ac; continue; }
    if (isJdSandbox(o)) { report.sandbox.rows++; report.sandbox.acres += ac; }
    hosts.add(o, o);
    report.kept.rows++; report.kept.acres += ac; kept.push(o);
  }
  report.bothHosts = hosts.both();
  return { rows: kept, report: roundReport(report) };
}

// Load statuses for a set of ids in chunks. `fetchChunk(ids)` returns op rows (or throws).
// A chunk that fails leaves its ids absent — those rows then read as UNVERIFIED (kept,
// counted), never as excluded.
// Ids are 42 safe characters, so a chunk of 100 is a ~4.5 KB query string. A book-wide read
// (Crop Review's double-insured scan) needs ~90 chunks, so they run a few at a time.
const JD_EVIDENCE_CHUNK = 100;
const JD_EVIDENCE_PARALLEL = 6;
//
// `cache` (optional Map id -> op row | null) lets one request share statuses across several
// reads. An id a chunk ANSWERED but did not return is cached as null (no operation row);
// an id whose chunk FAILED is not cached, so it stays unverified rather than settled.
async function loadJdEvidenceStatus(ids, fetchChunk, cache = null) {
  const out = new Map();
  const uniq = [...new Set((ids || []).filter((x) => x && !isManualOperationId(x)).map(String))];
  const list = [];
  for (const id of uniq) {
    if (cache && cache.has(id)) { const v = cache.get(id); if (v) out.set(id, v); }
    else list.push(id);
  }
  const chunks = [];
  for (let i = 0; i < list.length; i += JD_EVIDENCE_CHUNK) chunks.push(list.slice(i, i + JD_EVIDENCE_CHUNK));
  let next = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const part = chunks[next++];
      let got = [], okRead = true;
      try { got = (await fetchChunk(part)) || []; } catch (_) { got = []; okRead = false; }
      for (const o of got) if (o && o.jd_operation_id) out.set(String(o.jd_operation_id), o);
      if (cache && okRead) for (const id of part) cache.set(id, out.get(id) || null);
    }
  };
  await Promise.all(Array.from({ length: Math.min(JD_EVIDENCE_PARALLEL, chunks.length) }, worker));
  return out;
}

// Sum several reports (one request reading several tables) into one.
function mergeJdEvidenceReports(...reports) {
  const out = emptyReport();
  const both = new Map();
  for (const r of reports) {
    if (!r) continue;
    for (const k of REPORT_KEYS) if (r[k]) { out[k].rows += r[k].rows || 0; out[k].acres += r[k].acres || 0; }
    for (const b of r.bothHosts || []) both.set(`${b.farmer_id || ""}|${b.season}`, b);
  }
  out.bothHosts = [...both.values()];
  return roundReport(out);
}

// Convenience: rows in, kept rows + report out.
async function jdEvidenceOnly(rows, fetchChunk, opts, cache) {
  const st = await loadJdEvidenceStatus(jdEvidenceOpIds(rows), fetchChunk, cache || null);
  return filterJdEvidence(rows, st, opts);
}

module.exports = {
  JD_EVIDENCE_STATUS_COLUMNS, JD_EVIDENCE_CHUNK, isManualOperationId, jdEvidenceExclusion,
  jdEvidenceOpIds, filterJdEvidence, isJdEvidenceRow, isJdSandbox, jdSeasonOf, filterJdEvidenceOps, loadJdEvidenceStatus, jdEvidenceOnly,
  mergeJdEvidenceReports,
};
